import Container from "@/components/atoms/Container";
import Button from "@/components/atoms/Button";
import { router } from "@inertiajs/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/atoms/Accordion";
import { CheckboxButton } from "@/components/atoms/Checkbox";
import User from "@/types/User";
import { useEffect, useState } from "react";
import { CheckedState } from "@radix-ui/react-checkbox";
import clsx from "clsx";
import useAccess from "@/hooks/useAccess";

type Props = {
  user: User;
  countries: any[];
};

const CountriesTab = ({ countries, user }: Props) => {
  const { getAccess } = useAccess();
  const [selected, setSelected] = useState<string[]>([]);

  const handleCheck = (checked: CheckedState, id: string) => {
    setSelected(
      checked ? [...selected, id] : selected.filter((select) => select !== id)
    );
  };

  const handleSelectAll = (checked: CheckedState, subCountries: any[]) => {
    const subCountryIds = subCountries.map((subCountry) => `${subCountry.id}`);
    setSelected((prevSelected) =>
      checked
        ? [...prevSelected, ...subCountryIds.filter((id) => !prevSelected.includes(id))]
        : prevSelected.filter((id) => !subCountryIds.includes(id))
    );
  };

  const handleSave = () => {
    router.post(`/admin/users/${user.id}/set_user_countries`, {
      data: selected,
    });
  };

  useEffect(() => {
    const selectedIds = user?.countries.flatMap((country) =>
      country.children.length > 0
        ? country.children.map((child) => `${child.id}`)
        : [`${country.id}`]
    ) || [];

    setSelected(selectedIds);
  }, [user?.countries]);

  return (
    <Container className="py-4">
      {countries
        .filter((account) =>
          user.accounts.find((userAccount) => userAccount.id === account.id)
        )
        .map((account) => (
          <Accordion
            key={account.id}
            type="multiple"
            className="rounded mb-4"
          >
            <AccordionItem value={account.id} key={account.id}>
              <AccordionTrigger className="px-2 border border-border rounded">
                {account.name}
              </AccordionTrigger>
              <AccordionContent className="p-0">
                {/* Check if the first country has sub_countries */}
                {account.countries.length > 0 && account.countries[0].sub_countries?.length ? (
                  // If sub_countries exist
                  <>
                    {account.countries.map((country: any) => {
                      const countrySelected = selected.includes(`${country.id}`);
                      
                      return (
                        <div key={country.id}>
                          <Accordion type="multiple">
                            <AccordionItem
                              value={country.id}
                              className="border-0"
                            >
                              <AccordionTrigger className="p-3 bg-blue/20 dark:bg-gray-700 border border-border">
                                {country.name}
                              </AccordionTrigger>
                              <AccordionContent className="p-0">
                                {/* "Select All" for sub-countries */}
                                <CheckboxButton
                                  variant="text"
                                  disabled={!getAccess(["update_user_countries"])}
                                  className="hover:bg-slate-300 dark:hover:bg-slate-800 rounded-none dark:bg-slate-800 mb-2"
                                  labelClassName="font-bold"
                                  checked={country.sub_countries.every((sub: any) =>
                                    selected.includes(`${sub.id}`)
                                  )}
                                  onCheckedChange={(checked) =>
                                    handleSelectAll(
                                      checked,
                                      country.sub_countries
                                    )
                                  }
                                >
                                  Select All Sub-countries
                                </CheckboxButton>
                                <ul>
                                  {country.sub_countries.map(
                                    (sub_country: any) => {
                                      const subSelected = selected.includes(
                                        `${sub_country.id}`
                                      );

                                      return (
                                        <li key={sub_country.id}>
                                          <CheckboxButton
                                            variant="text"
                                            disabled={!getAccess(['update_user_countries'])}
                                            className="hover:bg-slate-300 dark:hover:bg-blue rounded-none dark:bg-slate-800"
                                            labelClassName={clsx(
                                              "flex justify-between w-full",
                                              {
                                                "font-bold": subSelected,
                                              }
                                            )}
                                            checked={subSelected}
                                            onCheckedChange={(checked) =>
                                              handleCheck(checked, sub_country.id.toString())
                                            }
                                          >
                                            {sub_country.name}
                                          </CheckboxButton>
                                        </li>
                                      );
                                    }
                                  )}
                                </ul>
                              </AccordionContent>
                            </AccordionItem>
                          </Accordion>
                        </div>
                      );
                    })}
                  </>
                ) : (
                  // If no sub_countries
                  <>
                    <CheckboxButton
                      variant="text"
                      className="hover:bg-slate-300 dark:hover:bg-blue rounded-none dark:bg-slate-700 mb-2"
                      labelClassName="font-bold"
                      checked={account.countries.every((country: any) =>
                        selected.includes(`${country.id}`)
                      )}
                      onCheckedChange={(checked) =>
                        handleSelectAll(
                          checked,
                          account.countries
                        )
                      }
                    >
                      Select All Countries
                    </CheckboxButton>

                    {account.countries.map((country: any) => {
                      const countrySelected = selected.includes(`${country.id}`);
                      
                      return (
                        <div key={country.id}>
                          <CheckboxButton
                            variant="text"
                            className="hover:bg-slate-300 dark:hover:bg-blue rounded-none dark:bg-slate-800"
                            labelClassName={clsx("flex justify-between w-full", {
                              "font-bold": countrySelected,
                            })}
                            checked={countrySelected}
                            onCheckedChange={(checked) =>
                              handleCheck(checked, country.id.toString())
                            }
                          >
                            {country.name}
                          </CheckboxButton>
                        </div>
                      );
                    })}
                  </>
                )}
                  
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ))}

      <Button onClick={handleSave} className="mt-4" disabled={!getAccess(['update_user_countries'])}>
        Save
      </Button>
    </Container>
  );
};

export default CountriesTab;
